import {
  Banner,
  Checkbox,
  Chip,
  Tooltip,
  ColorsV2,
  useThemeColors,
  Flex,
  Text,
  Country,
} from '@electricjs/arc';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { skipToken } from '@reduxjs/toolkit/query';

import { ABMChipContainer } from '@/components/ABMManagement/ABMChipStatus';
import { ResellerIdsList } from '@/components/ABMManagement/ABMComponents';
import { ConfigurationDetailPane } from '@/components/Storefront/components/ConfigurationDetailPane';
import { CA_ABM_RESELLER_ID } from '@/constants/abm';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import {
  useConfirmOrganizationResellerMutation,
  useGetOrganizationResellersQuery,
  useGetResellerByAbmIdQuery,
} from '@/redux/slices/organizationApiSlice';

const CANADA_COUNTRY_CODE: Country = 'CA';

type ABMConnectedMessageProps = {
  storeCountry?: string;
};

const ABMConnectedMessage = ({ storeCountry }: ABMConnectedMessageProps) => {
  const { t } = useTranslation('getting-started');
  const [successChipColor] = useThemeColors([ColorsV2.SUCCESS_LIGHTEST]);
  const { abmCanadaResellerId } = useFlags();
  const organizationId = useGetOrganizationId();
  const { showErrorToast } = useGlobalUI();
  const [isCaResellerChecked, setIsCaResellerChecked] = useState(false);

  const isCanadaOrg = storeCountry === CANADA_COUNTRY_CODE;
  const shouldCheckCaReseller = Boolean(abmCanadaResellerId) && isCanadaOrg;

  const { data: resellers, isFetching: isFetchingResellers } =
    useGetOrganizationResellersQuery(
      shouldCheckCaReseller ? { organizationId } : skipToken
    );

  const [confirmReseller, { isLoading: isConfirmingReseller }] =
    useConfirmOrganizationResellerMutation();

  const hasCaReseller =
    resellers?.some(reseller => reseller.countryCode === CANADA_COUNTRY_CODE) ??
    false;

  const showCanadaResellerBanner =
    shouldCheckCaReseller && !isFetchingResellers && !hasCaReseller;

  const { data: caReseller, isFetching: isFetchingCaReseller } =
    useGetResellerByAbmIdQuery(
      showCanadaResellerBanner
        ? {
            organizationId,
            abmResellerId: CA_ABM_RESELLER_ID,
            countryCode: CANADA_COUNTRY_CODE,
          }
        : skipToken
    );

  const handleConfirmCaReseller = (checked: boolean) => {
    if (!checked) return;

    if (!caReseller?.id) {
      showErrorToast({
        id: 'abm-canada-reseller-confirm-error-toast',
        message: t('abmConnectedMessage.canadaResellerBanner.confirmError'),
      });
      return;
    }

    setIsCaResellerChecked(true);
    confirmReseller({
      organizationId,
      resellerIds: [caReseller.id],
    })
      .unwrap()
      .catch(() => {
        setIsCaResellerChecked(false);
        showErrorToast({
          id: 'abm-canada-reseller-confirm-error-toast',
          message: t('abmConnectedMessage.canadaResellerBanner.confirmError'),
        });
      });
  };

  return (
    <ConfigurationDetailPane
      id="apple-ztp-status-card"
      testId="apple-ztp-status-card">
      <Flex vertical rowGap="1.5rem" width="100%">
        <Flex alignItems="center" gap="1rem">
          <ABMChipContainer>
            <Tooltip
              maxWidth="40rem"
              content={t('abmConnectedMessage.tooltipContent')}
              placement="top-start">
              <Chip
                id="abm-enrollment-success-chip"
                backgroundColor={successChipColor}
                variant="small"
                height="2rem"
                pt="0.2rem"
                textColor={ColorsV2.SUCCESS_DARK}>
                {t('abmConnectedMessage.chipLabel')}
              </Chip>
            </Tooltip>
          </ABMChipContainer>
        </Flex>
        {showCanadaResellerBanner && (
          <Banner
            id="abm-canada-reseller-banner"
            isFlat
            intent="warning"
            title={t('abmConnectedMessage.canadaResellerBanner.title')}
            message={() => (
              <Flex vertical rowGap="1rem">
                <Text>
                  {t('abmConnectedMessage.canadaResellerBanner.message')}
                </Text>
                <ResellerIdsList
                  resellers={[
                    {
                      id: CA_ABM_RESELLER_ID,
                      label: t('abmSetupForm.step3.caResellerLabel'),
                    },
                  ]}
                />
                <Checkbox
                  id="abm-canada-reseller-confirm-checkbox"
                  checked={isCaResellerChecked}
                  disabled={
                    isConfirmingReseller ||
                    isFetchingResellers ||
                    isFetchingCaReseller
                  }
                  onChange={e =>
                    handleConfirmCaReseller(e.currentTarget.checked)
                  }>
                  {t(
                    'abmConnectedMessage.canadaResellerBanner.confirmCheckbox'
                  )}
                </Checkbox>
              </Flex>
            )}
          />
        )}
      </Flex>
    </ConfigurationDetailPane>
  );
};

export default ABMConnectedMessage;
